import {
    PutEffect,
    all,
    call,
    fork,
    put,
    select,
    takeLatest
} from 'redux-saga/effects';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { SagaIterator } from 'redux-saga';
import diff from 'lodash.difference';
import keyBy from 'lodash.keyby';

import { takeLatestWithCancel } from 'utils/redux-saga';
import axiosPoll from 'utils/axiosPoll';
// Global Selectors
// @ts-ignore
import { idTokenSelector } from 'services/auth/selectors';
// @ts-ignore
import { selectCurrentOrganizationId } from 'services/currentOrganizationId/selectors';

// Global Services
import {
    setSubmitHasFailed,
    deleteItems
} from 'services/virtualizedTable/actions';
import { updateQueryString } from 'services/router/actions';

// @ts-ignore
import { enqueueSnackbar } from 'services/notifications/actions';

import { ERROR_MODAL_QUERY_STRING_PARAM } from 'components/ErrorModal/constants';

// Local Types
import {
    RequestCreateCommodityAction,
    RequestCommodityUpdateAction,
    RequestDeleteCommodityAction,
    Commodity
} from './types';

// Local Constants
import {
    COMMODITY_CREATE_REQUESTED,
    COMMODITY_CREATE_NAMESPACE,
    COMMODITY_UPDATE_NAMESPACE,
    COMMODITY_UPDATE_REQUESTED,
    COMMODITY_DELETE_NAMESPACE,
    COMMODITY_DELETE_REQUESTED
} from './constants';

// Local Actions
import {
    succeededCreateCommodity,
    failedCreateCommodity,
    succeededUpdateCommodity,
    failedUpdateCommodity,
    setCommodities,
    succeededDeleteCommodity,
    failedDeleteCommodity,
    removeCommodities
} from './actions';

// Environment
const { REACT_APP_API_URL } = process.env;

export function* createCommodity(
    action: RequestCreateCommodityAction
): SagaIterator {
    const idToken = yield select(idTokenSelector);
    const url = `${REACT_APP_API_URL}/v1/Commodities`;
    try {
        const response = yield call(axios, url, {
            method: 'post',
            headers: {
                Authorization: idToken
            },
            data: action.payload
        });
        const {
            data: { id, name }
        } = response;

        yield all([
            put(succeededCreateCommodity(response)),
            put(
                setCommodities({
                    [id]: {
                        id,
                        name
                    }
                })
            )
        ]);
    } catch (error) {
        yield all([
            put(failedCreateCommodity(error)),
            put(setSubmitHasFailed(true)),
            put(
                updateQueryString({
                    [ERROR_MODAL_QUERY_STRING_PARAM]: COMMODITY_CREATE_NAMESPACE
                })
            )
        ]);
    }
}

export function* watchCommodityCreateRequested(): SagaIterator {
    yield takeLatest(COMMODITY_CREATE_REQUESTED, createCommodity);
}

export function* updateCommodity(
    action: RequestCommodityUpdateAction
): SagaIterator {
    const idToken = yield select(idTokenSelector);
    const { id, ...payload } = action.payload;

    try {
        const url = `${REACT_APP_API_URL}/v1/Commodities/${id}`;
        const response = yield call(axios, url, {
            method: 'patch',
            headers: {
                Authorization: idToken
            },
            data: payload
        });
        const { data: commodity } = response;

        yield all([
            put(succeededUpdateCommodity(response)),
            put(
                setCommodities({
                    [commodity.id]: commodity
                })
            )
        ]);
    } catch (error) {
        yield all([
            put(failedUpdateCommodity(error)),
            put(setSubmitHasFailed(true)),
            put(
                updateQueryString({
                    [ERROR_MODAL_QUERY_STRING_PARAM]: COMMODITY_UPDATE_NAMESPACE
                })
            )
        ]);
    }
}

export function* watchCommodityUpdateRequested(): SagaIterator {
    yield takeLatest(COMMODITY_UPDATE_REQUESTED, updateCommodity);
}

export function* deleteCommodity(
    action: RequestDeleteCommodityAction
): SagaIterator {
    const idToken = yield select(idTokenSelector);
    const { id } = action.payload;
    const url = `${REACT_APP_API_URL}/v1/Commodities/${id}`;

    try {
        const response = yield call(axios, url, {
            method: 'delete',
            headers: {
                Authorization: idToken
            }
        });

        yield all([
            put(succeededDeleteCommodity(response)),
            put(deleteItems([id.toString()])),
            put(removeCommodities([id])),
            put(
                enqueueSnackbar({
                    message: 'Commodity deleted successfully',
                    options: { variant: 'success' }
                })
            )
        ]);
    } catch (error) {
        yield all([
            put(failedDeleteCommodity(error)),
            put(setSubmitHasFailed(true)),
            put(
                updateQueryString({
                    [ERROR_MODAL_QUERY_STRING_PARAM]: COMMODITY_DELETE_NAMESPACE
                })
            )
        ]);
    }
}

export function* watchCropsurePasswordDeleteRequested(): SagaIterator {
    yield takeLatest(COMMODITY_DELETE_REQUESTED, deleteCommodity);
}

export default function* commoditiesSagas(): SagaIterator {
    yield all([
        fork(watchCommodityCreateRequested),
        fork(watchCommodityUpdateRequested),
        fork(watchCropsurePasswordDeleteRequested)
    ]);
}
